import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '../App';
import Layout from '../components/Layout';
import { Receipt, Download, Edit, Eye, Trash2, Search, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import LiquidacionViewModal from './liquidaciones/LiquidacionViewModal';
import LiquidacionEditModal from './liquidaciones/LiquidacionEditModal';
import LiquidacionPDF from './liquidaciones/LiquidacionPDF';

const API = process.env.REACT_APP_BACKEND_URL;

const Liquidaciones = () => {
  const { user, token } = useAuth();
  const [liquidaciones, setLiquidaciones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLiquidacion, setSelectedLiquidacion] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  // Individual filters
  const [filterDespacho, setFilterDespacho] = useState('');
  const [filterTransportista, setFilterTransportista] = useState('all');
  const [filterImportadora, setFilterImportadora] = useState('all');
  const [filterFecha, setFilterFecha] = useState('');

  const fetchLiquidaciones = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/liquidaciones`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLiquidaciones(response.data);
    } catch (error) {
      console.error('Error fetching liquidaciones:', error);
      toast.error('Error al cargar liquidaciones');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchLiquidaciones();
  }, [fetchLiquidaciones]);

  const handleView = (liquidacion) => {
    setSelectedLiquidacion(liquidacion);
    setShowViewModal(true);
  };

  const handleEdit = (liquidacion) => {
    setSelectedLiquidacion(liquidacion);
    setShowEditModal(true);
  };

  const handleDownloadPDF = async (liquidacion) => {
    try {
      toast.info('Generando PDF...');
      await LiquidacionPDF.generate(liquidacion, token);
      toast.success('PDF generado exitosamente');
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Error al generar PDF');
    }
  };

  const handleDelete = async (liquidacion) => {
    if (!window.confirm(`¿Está seguro de eliminar la liquidación ${liquidacion.dispatch_number}-${liquidacion.company_name}?`)) {
      return;
    }
    
    const justification = window.prompt('Ingrese la justificación para eliminar esta liquidación:');
    if (!justification) {
      toast.error('La justificación es obligatoria');
      return;
    }

    try {
      await axios.delete(`${API}/api/liquidaciones/${liquidacion.id}?justification=${encodeURIComponent(justification)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Liquidación eliminada');
      fetchLiquidaciones();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    }
  };

  const handleEditSave = async (data, justification) => {
    try {
      await axios.put(`${API}/api/liquidaciones/${selectedLiquidacion.id}`, {
        data,
        justification
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Liquidación actualizada');
      setShowEditModal(false);
      fetchLiquidaciones();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar');
    }
  };

  const filteredLiquidaciones = useMemo(() => {
    let result = [...liquidaciones];
    if (filterDespacho) result = result.filter(l => l.dispatch_number?.toLowerCase().includes(filterDespacho.toLowerCase()));
    if (filterTransportista !== 'all') result = result.filter(l => (l.company_alias || l.company_name) === filterTransportista);
    if (filterImportadora !== 'all') result = result.filter(l => l.importadora_razon_social === filterImportadora);
    if (filterFecha) result = result.filter(l => l.created_at?.startsWith(filterFecha));
    // Auto-sort by dispatch_number
    result.sort((a, b) => (a.dispatch_number || '').localeCompare(b.dispatch_number || ''));
    return result;
  }, [liquidaciones, filterDespacho, filterTransportista, filterImportadora, filterFecha]);

  const uniqueTransportistas = useMemo(() => {
    const names = [...new Set(liquidaciones.map(l => l.company_alias || l.company_name).filter(Boolean))];
    return names.sort();
  }, [liquidaciones]);

  const uniqueImportadoras = useMemo(() => {
    const names = [...new Set(liquidaciones.map(l => l.importadora_razon_social).filter(Boolean))];
    return names.sort();
  }, [liquidaciones]);

  const clearFilters = () => {
    setFilterDespacho('');
    setFilterTransportista('all');
    setFilterImportadora('all');
    setFilterFecha('');
  };

  const hasFilters = filterDespacho || filterTransportista !== 'all' || filterImportadora !== 'all' || filterFecha;

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-CL', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const formatCurrency = (value, currency = 'CLP') => {
    if (!value) return '-';
    if (currency === 'USD') {
      return `$${value.toLocaleString('es-CL')} USD`;
    }
    return `$${Math.round(value).toLocaleString('es-CL')}`;
  };

  return (
    <Layout>
    <div className="space-y-4" data-testid="liquidaciones-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Receipt className="h-6 w-6 text-emerald-600" />
          <div>
            <h1 className="text-xl font-bold text-slate-800">Liquidaciones de Flete</h1>
            <p className="text-xs text-slate-500">
              {user.role === 'MASTER' ? 'Gestión completa' : 'Visualización'} de liquidaciones
            </p>
          </div>
        </div>
      </div>

      {/* Individual Filters */}
      <div className="bg-white border border-slate-200 rounded-lg p-3 flex flex-wrap gap-2 items-end">
        <div className="flex flex-col gap-1">
          <span className="text-[10px] font-semibold text-slate-500 uppercase">Despacho</span>
          <Input placeholder="N° Despacho..." value={filterDespacho} onChange={e => setFilterDespacho(e.target.value)} className="h-7 text-xs w-32" data-testid="filter-despacho" />
        </div>
        <div className="flex flex-col gap-1">
          <span className="text-[10px] font-semibold text-slate-500 uppercase">Transportista</span>
          <Select value={filterTransportista} onValueChange={setFilterTransportista}>
            <SelectTrigger className="h-7 text-xs w-40"><SelectValue placeholder="Todos" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              {uniqueTransportistas.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-col gap-1">
          <span className="text-[10px] font-semibold text-slate-500 uppercase">Importadora</span>
          <Select value={filterImportadora} onValueChange={setFilterImportadora}>
            <SelectTrigger className="h-7 text-xs w-40"><SelectValue placeholder="Todas" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas</SelectItem>
              {uniqueImportadoras.map(i => <SelectItem key={i} value={i}>{i}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-col gap-1">
          <span className="text-[10px] font-semibold text-slate-500 uppercase">Fecha (creación)</span>
          <Input type="date" value={filterFecha} onChange={e => setFilterFecha(e.target.value)} className="h-7 text-xs w-36" data-testid="filter-fecha-liq" />
        </div>
        {hasFilters && (
          <Button variant="ghost" size="sm" onClick={clearFilters} className="h-7 text-xs text-slate-500 self-end">
            <X size={12} className="mr-1" /> Limpiar
          </Button>
        )}
        <span className="text-xs text-slate-400 self-end ml-auto">{filteredLiquidaciones.length} resultados</span>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-slate-500">Cargando liquidaciones...</div>
        ) : filteredLiquidaciones.length === 0 ? (
          <div className="p-8 text-center text-slate-500">No hay liquidaciones que coincidan con los filtros</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-100 border-b border-slate-200">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold text-slate-600">DESPACHO</th>
                  <th className="px-3 py-2 text-left font-semibold text-slate-600">TRANSPORTISTA</th>
                  <th className="px-3 py-2 text-left font-semibold text-slate-600">IMPORTADORA</th>
                  <th className="px-3 py-2 text-left font-semibold text-slate-600">FECHA</th>
                  <th className="px-3 py-2 text-left font-semibold text-slate-600">FAC. TRANSPORTE</th>
                  <th className="px-3 py-2 text-right font-semibold text-slate-600">CAMIONES</th>
                  <th className="px-3 py-2 text-right font-semibold text-slate-600">TOTAL USD</th>
                  <th className="px-3 py-2 text-right font-semibold text-slate-600">A PAGAR</th>
                  <th className="px-3 py-2 text-center font-semibold text-slate-600">ACCIONES</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredLiquidaciones.map((liquidacion) => (
                  <tr key={liquidacion.id} className="hover:bg-slate-50">
                    <td className="px-3 py-2 font-mono font-semibold text-slate-800">{liquidacion.dispatch_number}</td>
                    <td className="px-3 py-2 text-slate-700">{liquidacion.company_alias || liquidacion.company_name}</td>
                    <td className="px-3 py-2 text-slate-600">{liquidacion.importadora_razon_social}</td>
                    <td className="px-3 py-2 text-slate-600 font-mono">{formatDate(liquidacion.created_at)}</td>
                    <td className="px-3 py-2 font-mono text-slate-500">{liquidacion.factura_transporte || <span className="text-slate-300">—</span>}</td>
                    <td className="px-3 py-2 text-right font-mono text-slate-700">{liquidacion.trucks?.length || 0}</td>
                    <td className="px-3 py-2 text-right font-mono text-emerald-600 font-semibold">{formatCurrency(liquidacion.total_usd, 'USD')}</td>
                    <td className="px-3 py-2 text-right font-mono text-blue-600 font-semibold">{formatCurrency(liquidacion.a_pagar, 'USD')}</td>
                    <td className="px-3 py-2">
                      <div className="flex items-center justify-center gap-1">
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-500 hover:text-blue-600" onClick={() => handleView(liquidacion)} title="Ver detalle" data-testid={`view-liquidacion-${liquidacion.id}`}><Eye size={14} /></Button>
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-500 hover:text-emerald-600" onClick={() => handleDownloadPDF(liquidacion)} title="Descargar PDF" data-testid={`download-liquidacion-${liquidacion.id}`}><Download size={14} /></Button>
                        {user.role === 'MASTER' && (
                          <>
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-500 hover:text-amber-600" onClick={() => handleEdit(liquidacion)} title="Editar" data-testid={`edit-liquidacion-${liquidacion.id}`}><Edit size={14} /></Button>
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-500 hover:text-red-600" onClick={() => handleDelete(liquidacion)} title="Eliminar" data-testid={`delete-liquidacion-${liquidacion.id}`}><Trash2 size={14} /></Button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modals */}
      {showViewModal && selectedLiquidacion && (
        <LiquidacionViewModal liquidacion={selectedLiquidacion} onClose={() => setShowViewModal(false)} onDownload={() => handleDownloadPDF(selectedLiquidacion)} />
      )}
      {showEditModal && selectedLiquidacion && (
        <LiquidacionEditModal liquidacion={selectedLiquidacion} onClose={() => setShowEditModal(false)} onSave={handleEditSave} />
      )}
    </div>
    </Layout>
  );
};

export default Liquidaciones;
